import type { WeatherData } from '@/lib/weather';
import type { PlantLike } from '@/lib/plantUtils';
import { getEffectiveTempRequirements } from '@/lib/plantUtils';

export type PlantingWarningType = 'too-cold' | 'too-hot' | 'hard-frost';

export interface PlantingWarning {
  type: PlantingWarningType;
  severity: 'warning' | 'info';
  message: string;
  weekMinTemp?: number;
  minNightTempF?: number;
}

/**
 * Checks whether current/forecast weather conditions are suitable for planting
 * the given plant outdoors. Returns a warning object if conditions are poor,
 * or null if conditions are fine.
 *
 * Used by both the dashboard planting form (MyPlants) and the plant detail page
 * Log a Planting button.
 */
export function checkPlantingConditions(
  plant: PlantLike & { name?: string },
  weatherData: WeatherData | null,
): PlantingWarning | null {
  if (!plant || !weatherData) return null;

  const req = getEffectiveTempRequirements(plant);
  const weekMinTemp = Math.min(
    weatherData.today.minTemp,
    ...weatherData.forecast.slice(0, 7).map(d => d.minTemp),
  );
  const currentMaxTemp = weatherData.today.maxTemp;
  const plantName = (plant as { name?: string }).name ?? 'this plant';

  if (!req.frostTolerant && weekMinTemp < req.minNightTempF) {
    return {
      type: 'too-cold',
      severity: 'warning',
      message: `Heads up - nights are dropping to ${weekMinTemp}°F this week and ${plantName} needs nights consistently above ${req.minNightTempF}°F. It is too cold to plant ${plantName} outside right now.`,
      weekMinTemp,
      minNightTempF: req.minNightTempF,
    };
  }

  if (req.season === 'cool' && req.heatSensitiveAboveF && currentMaxTemp >= req.heatSensitiveAboveF) {
    return {
      type: 'too-hot',
      severity: 'warning',
      message: `${plantName} is a cool-season crop and current temperatures of ${currentMaxTemp}°F are too warm - it will bolt quickly. Consider waiting until late summer for a fall planting.`,
    };
  }

  if (req.frostTolerant && !req.hardFrostTolerant && weekMinTemp < 25) {
    return {
      type: 'hard-frost',
      severity: 'info',
      message: `Hard frost is forecast this week (${weekMinTemp}°F). ${plantName} handles light frost but may need protection during a hard freeze.`,
    };
  }

  return null;
}
